import { s } from '@/theme/tokens';
import { ToggleGroup, ToggleItem } from '@/ui';
import { Box, Cancel, IconButton, Text, Tooltip } from '@/ui/primitives';
import { EventOutlinedIcon } from '@/ui/primitives/icons';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { MouseEvent, ReactElement } from 'react';
type MetricsToolbarDateControlsProps = {
  cloneTooltipSlotProps: {
    tooltip: {
      sx: {
        fontSize: string;
        lineHeight: number;
      };
    };
  };
  dateQuickFilter: DateQuickFilter;
  dateRangeLabel: string;
  hasActiveToolbarState: boolean;
  hasCustomDateRange: boolean;
  hideDateRangeLabel: boolean;
  includeLatestQuickFilter: boolean;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  onOpenCustomDateFilters: (event: MouseEvent<HTMLElement>) => void;
  onResetToolbarFilters: () => void;
  preserveWindowScroll: (action: () => void) => void;
  toolbarControlSize: number;
  toolbarIconButtonSx: object | object[];
  toolbarIconCircleSx: (selected: boolean) => object;
  toolbarIconTooltipDelayMs: number;
};

const quickFilterTextSx = {
  fontSize: (muiTheme: { typography: { pxToRem: (value: number) => string } }) =>
    muiTheme.typography.pxToRem(15),
  lineHeight: 1.4,
} as const;

const dateRangeLabelSx = {
  color: 'text.secondary',
  fontSize: (muiTheme: { typography: { pxToRem: (value: number) => string } }) =>
    muiTheme.typography.pxToRem(15),
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
} as const;

export const MetricsToolbarDateControls = ({
  cloneTooltipSlotProps,
  dateQuickFilter,
  dateRangeLabel,
  hasActiveToolbarState,
  hasCustomDateRange,
  hideDateRangeLabel,
  includeLatestQuickFilter,
  onDateQuickFilterChange,
  onOpenCustomDateFilters,
  onResetToolbarFilters,
  preserveWindowScroll,
  toolbarControlSize,
  toolbarIconButtonSx,
  toolbarIconCircleSx,
  toolbarIconTooltipDelayMs,
}: MetricsToolbarDateControlsProps): ReactElement => (
  <>
    {dateRangeLabel && !hideDateRangeLabel ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Text role="body" sx={dateRangeLabelSx}>
          {dateRangeLabel}
        </Text>
      </Box>
    ) : null}
    <ToggleGroup
      size="small"
      value={hasCustomDateRange ? 'custom' : dateQuickFilter}
      onChange={(_event, nextValue: DateQuickFilter | 'custom' | null) => {
        if (!nextValue || nextValue === 'custom') {
          return;
        }
        preserveWindowScroll(() => {
          onDateQuickFilterChange(nextValue);
        });
      }}
      sx={{
        flexShrink: 0,
        ml: s('sm'),
        '& .MuiToggleButton-root': {
          backgroundColor: 'background.default',
          minWidth: 0,
          px: s('md'),
          py: s('none'),
          height: `${toolbarControlSize}px`,
        },
        '& .MuiToggleButton-root:hover': {
          backgroundColor: 'background.default',
        },
      }}
    >
      {includeLatestQuickFilter ? (
        <ToggleItem value="latest">
          <Text role="body" component="span" sx={quickFilterTextSx}>
            Latest
          </Text>
        </ToggleItem>
      ) : null}
      <ToggleItem value="7d">
        <Text role="body" component="span" sx={quickFilterTextSx}>
          7d
        </Text>
      </ToggleItem>
      <ToggleItem value="14d">
        <Text role="body" component="span" sx={quickFilterTextSx}>
          14d
        </Text>
      </ToggleItem>
      <ToggleItem value="30d">
        <Text role="body" component="span" sx={quickFilterTextSx}>
          30d
        </Text>
      </ToggleItem>
      <Tooltip
        title="Custom Dates"
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={cloneTooltipSlotProps}
      >
        <ToggleItem
          value="custom"
          aria-label="Open custom date filters"
          onClick={onOpenCustomDateFilters}
          sx={{
            minWidth: 0,
            width: `${toolbarControlSize}px`,
            px: s('none'),
          }}
        >
          <EventOutlinedIcon
            sx={{
              width: '20px !important',
              height: '20px !important',
            }}
          />
        </ToggleItem>
      </Tooltip>
    </ToggleGroup>
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
    >
      <Tooltip
        title="Reset"
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={cloneTooltipSlotProps}
      >
        <Box component="span">
          <IconButton
            size="medium"
            aria-label="Reset chart filters"
            onClick={onResetToolbarFilters}
            disabled={!hasActiveToolbarState}
            sx={[toolbarIconButtonSx, hasActiveToolbarState ? { color: 'primary.main' } : null]}
          >
            <Box sx={toolbarIconCircleSx(false)}>
              <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
            </Box>
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  </>
);
